import { gql } from "@apollo/client";
import { Chip } from "@mui/material";
import Stack from "atoms/Stack";
import TextWithTooltip from "atoms/TextWithTooltip";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING } from "helpers/spacings";
import useData from "helpers/useData";
import Section from "molecules/Section";
import StatsTable, {
  StatsTableHeaderCell,
  StatsTableRow,
  StatsTableStatCell,
  StatsTableStatCellFragment,
} from "molecules/StatsTable";
import { StatValueFragment } from "molecules/StatValue";
import React, { useState } from "react";

export const BoxScoreFragment = gql`
  fragment BoxScoreFragment on Game {
    participations {
      id
      team {
        id
        name
      }
    }
  }
`;

export default function BoxScore({ game }) {
  const [data] = useData(
    gql`
      query BoxScore($gameId: ID!) {
        game(id: $gameId) {
          id
          participations {
            id
            isHomeTeam
            team {
              id
              name
            }
            overviewStats {
              shortName
              name
              ...StatValueFragment
            }
            gamePlayers {
              id
              playerNumber
              person {
                id
                fullNameWithAnonymisation
              }
              boxScoreStats {
                shortName
                name
                ...StatsTableStatCellFragment
              }
            }
            boxScoreTotalStats {
              shortName
              name
              ...StatsTableStatCellFragment
            }
          }
        }
      }
      ${StatValueFragment}
      ${StatsTableStatCellFragment}
    `,
    { gameId: game?.id },
    { skip: !game },
  );

  let homeParticipation = data?.game?.participations.find((participation) => participation.isHomeTeam);
  let awayParticipation = data?.game?.participations.find((participation) => !participation.isHomeTeam);

  // if there is no gamePlayer
  homeParticipation ||= data?.game?.participations[0];
  awayParticipation ||= data?.game?.participations[0];
  const homeTeamName = homeParticipation?.team.name || "Home Team";
  const awayTeamName = awayParticipation?.team.name || "Away Team";

  const [selectedTeam, selectedTeamSet] = useState("home");

  const activeParticipation = selectedTeam === "home" ? homeParticipation : awayParticipation;
  const firstRow = activeParticipation?.gamePlayers[0];

  return (
    <Section title="BOX SCORE" labels>
      <Stack style={{ gap: SPACING }}>
        <Stack horizontal dense alignItemsCenter>
          {[
            { key: "home", label: homeTeamName },
            { key: "away", label: awayTeamName },
          ].map(({ key, label }) => {
            const active = selectedTeam === key;
            return (
              <Chip
                key={key}
                size="small"
                variant="outlined"
                label={label}
                onClick={() => selectedTeamSet(key)}
                style={{ fontFamily: FONT_FAMILY_GRAPHIK, fontSize: 11 }}
                sx={{
                  borderColor: active ? "#000000" : "#B7B7B7",
                  backgroundColor: active ? "#000000" : "#FCFCFC",
                  color: active ? "#FFFFFF" : "inherit",
                  fontWeight: active ? 500 : 400,
                  "&&:hover": {
                    backgroundColor: active ? "#000000" : "#EFEFEF",
                  },
                }}
              />
            );
          })}
        </Stack>
        <StatsTable
          headerColumns={["Players"]}
          columns={firstRow?.boxScoreStats.map((columnStat) => (
            <TextWithTooltip tooltip={columnStat.name} key={columnStat.shortName}>
              {columnStat.shortName}
            </TextWithTooltip>
          ))}
        >
          {activeParticipation?.gamePlayers.map((gamePlayer, rowIndex) => (
            <StatsTableRow key={gamePlayer.id} rowIndex={rowIndex}>
              <StatsTableHeaderCell>
                {gamePlayer.person.fullNameWithAnonymisation} - #{gamePlayer.playerNumber || "?"}
              </StatsTableHeaderCell>
              {gamePlayer.boxScoreStats.map((stat) => (
                <StatsTableStatCell key={stat.shortName} stat={stat} />
              ))}
            </StatsTableRow>
          ))}
          <StatsTableRow summary>
            <StatsTableHeaderCell>(Total)</StatsTableHeaderCell>
            {activeParticipation?.boxScoreTotalStats.map((stat) => (
              <StatsTableStatCell key={stat.shortName} stat={stat} />
            ))}
          </StatsTableRow>
        </StatsTable>
      </Stack>
    </Section>
  );
}
